import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { P2Alert, P2Checkbox, P2TextLabel, P2Modal, P2Button, P2TextDescription } from "react-bootstrap-common-script";
import FileSelectionContainer from "../../../components/container/file-selection-container/file-selection-container";
import { DocumentItem, FileItem } from "../../../models/polymer/Document.model";
import { reduce } from "lodash";
import FileUploadingButton from "../../../components/button/file-uploading-button/file-uploading-button";
import { StageDCommentsInfo } from "../../../models/classes/stage-d/Review-Application.class";
import { downloadFile } from "../../../utils/download-file";
import { DownloadSvg } from "../../../assets/svg";
import { useAppSelector } from "../../../redux/hook";
import he from "he";

interface FileSelectionSegmentProps {
  variant?: "default" | "download-button";
  files: FileItem[];
  handleFileChange?: (
    e: React.ChangeEvent<HTMLInputElement>,
    code: string,
    label: string,
    file?: File
  ) => void;
  assignFileFields?: (file_id: string, submitted_date: string, key: string, id:string)=>void;
  assignError?: (isError: boolean, id: string) => void;
  clearFileChange?: (id: string) => void;
  index?: number;
  additionalContent?: JSX.Element;
  additionalCheckbox: string[];
  additionalCheckboxOnClick?: (document_code: string) => void;
  document: DocumentItem;
  comment: StageDCommentsInfo[];
  disabled?:boolean;
  download_only?: boolean;
  require_label?: boolean;
  folderName?: string;
  download_key?: string;
}

const DocumentClipIcon = () => {
  return (
    <svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_3475_57281)">
        <path
          d="M9.70548 20.6324L20.4531 9.69196C23.1868 6.89435 27.626 6.89435 30.3597 9.69196C33.0933 12.4777 33.0933 17.0015 30.3597 19.7991L17.5559 32.8467C15.4999 34.942 12.1704 34.942 10.126 32.8467C8.06997 30.7515 8.06997 27.3586 10.126 25.2634L22.9298 12.2158C24.2966 10.8229 26.5162 10.8229 27.8831 12.2158C29.2499 13.6086 29.2499 15.8705 27.8831 17.2634L16.3176 29.0491"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_3475_57281">
          <rect width="25" height="28" fill="white" transform="translate(8 7)" />
        </clipPath>
      </defs>
    </svg>
  );
};

function FileSelectionSegment({download_key="", variant="default", index = 0, download_only=false, disabled=false, require_label = true, folderName="", ...props }: FileSelectionSegmentProps) {
  const isChecked = props.additionalCheckbox.includes(props.document.code);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteFileName, setDeleteFileName] = useState(String);
  const [fileId, setFileId] = useState(String);
  const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);
  const [selectedDeleteFileName, setSelectedDeleteFileName] = useState(String);

  useEffect(() => {
    setSelectedDeleteFileName(
      props.files.filter((item)=>item.file_content.id === fileId).length > 0
      ? props.files.filter((item)=>item.file_content.id === fileId)[0].file_content.file_name || deleteFileName
      : deleteFileName
    );
  }, [fileId])

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  },[]);

  const inputFile = useRef<HTMLInputElement | null>(null);
  const totalSize = reduce(
    props.files.map((e) => e.file_content.size),
    (sum, n: any) => {
      return sum + n;
    },
    0
  );

  const getToDeleteItemModal = useCallback((value: boolean)=> {
    setIsModalOpen(value);
  },[]);
  const getFileName = useCallback((value: string)=> {
    setDeleteFileName(value);
  }, []);
  
  const secondaryButtonFunction = useCallback(() => {
    if (props.clearFileChange) {
      props.clearFileChange(fileId);
    }
    closeModal();
    setFileId("");
  }, [props.clearFileChange, fileId]);
  const getFileId = useCallback((value: string) =>{
    setFileId(value);
  },[]);

  const handleDownload = useCallback((key: string) => () => {
      console.log("downloading:", key);
      downloadFile(key, singpassToken);
  }, [singpassToken])

  const handleClearFileChange = useCallback(() => {
    if (inputFile.current) {
      inputFile.current.value = "";
    }
  }, [inputFile])

  const handleUploadButtonClick = useCallback(() => {
    if (!isChecked) {
      inputFile.current?.click();
    }
  }, [inputFile, isChecked])

  const handleAdditionalCheckboxClick = useCallback((code: string) => () => {
    props?.additionalCheckboxOnClick?.(code);
  }, [props.additionalCheckboxOnClick])

  const handleFileUploadOnChange = useCallback((code: string, label: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if(inputFile.current?.files && inputFile.current?.files.length > 0){
      props?.handleFileChange?.(e, code, label, inputFile.current?.files[0]);
    }
  },[props.handleFileChange, inputFile])
  if(variant === "download-button"){
    return (
      <div>
        <div className="polymer-file-selection-segment">
          <div className="polymer-file-selection-segment__container polymer-file-selection-segment-stage-d">
            <div className="d-flex flex-column flex-fill">
              {props.comment.map((item, _index) => {
                const key = _index;
                return (
                  <Fragment key={key}>
                    <P2TextLabel
                      marginBottom="33px"
                      bold={400}
                      label={he.decode(item.text ?? "")}
                    />
                  </Fragment>
                )
              })
              }
            </div>
            <div className="d-flex flex-column gap-2">
              <div className="polymer-file-selection-segment__file-button">
                <P2Button
                  onClick={handleDownload(download_key)}
                  variant="button-icon"
                  endIcon={<DownloadSvg strokeColor='#FFF'/>}
                  width="100%"
                  disabled={disabled || isChecked}
                >
                  Download file
                </P2Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="polymer-file-selection-segment">
        {require_label && <P2TextDescription color="#000" title={`${props.document.label}`} /> }

        <div className="polymer-file-selection-segment__container polymer-file-selection-segment-stage-d">
          <div className="d-flex flex-column flex-fill">
            {/* Only display comment from HDB label if comments does not contain hardcoded solicitor request folder text.(solicitorFolders state in stage D step 1 landing) */}
            {props.comment.every(item => item.text !== "A legal document stating that the solicitor or law firm is representing for the party in a case.") && 
              <P2TextLabel
              marginBottom="8px"
              bold={700}
              label={"Comment from HDB:"}
            />
            }
            {props.comment.map((item,_index)=>{
              const key = _index;
              return (
                <Fragment key={key}>
                  <div style={{marginBottom:33}}>
                    <P2TextLabel
                      bold={400}
                      label={he.decode(item.text ?? "")}
                    />
                    {item.documents?.length > 0 && item.documents.map(e=>{
                      const key = e.key;
                      return (<P2Button key={key} onClick={handleDownload(key)} variant="text-icon" endIcon={<DownloadSvg/>} width="100%" disabled={false} style={{ fontSize: '1rem' , justifyContent:'start'}}>Download file</P2Button>)
                    })}
                  </div>
                </Fragment>
                )
              })
            }
          </div>
          <div className="d-flex flex-column gap-2">
            <FileSelectionContainer size={totalSize}>
              {props.files.length > 0 &&
                isChecked === false &&
                props.files.map((file) => {
                  return (
                    <FileUploadingButton
                      id={file.file_content.id}
                      assignFileFields={props.assignFileFields}
                      assignError={props.assignError}
                      clearFileChange={handleClearFileChange}
                      type={2}
                      folderName={folderName}
                      getToDeleteItemModal={getToDeleteItemModal}
                      getFileName={getFileName}
                      getFileId={getFileId}
                      key={file.file_content.id}
                      file={file}
                    />
                  );
                })}
            </FileSelectionContainer>
            {props.files.some(item => item.is_error) &&             
            <div style={{maxWidth: "304px"}}>
              <P2Alert 
                text={<p style={{margin: 0, fontSize: "1rem", fontWeight: 400, lineHeight: "1.25rem"}}>File did not upload successfully. Please re-upload your file.</p>}
                variant={"danger"} 
              />
            </div>}
            <div className="polymer-file-selection-segment__file-button">
              <P2Button variant="button-icon" endIcon={<DocumentClipIcon />} width="100%" disabled={disabled || isChecked} onClick={handleUploadButtonClick}>
                Choose files
              </P2Button>
            </div>
            

            <div className="polymer-file-selection-segment__requirements">
              <p>{`Supported file formats are:`}</p>
              <p>{`PDF, JPG and PNG (maximum 5 MB per file)`}</p>
              <p>Maximum 10 MB combined</p>
              <p>Multiple documents may be uploaded</p>
            </div>
          </div>
        </div>
      </div>
      {props.document.checkbox && (
        <div className="d-flex flex-column gap-4">
          <div className="polymer-file-selection-segment__checkbox">
            <P2Checkbox
              isActive={isChecked}
              onClick={handleAdditionalCheckboxClick(props.document.code)}
              description={
                <p className="polymer-file-selection-segment__checkbox-text">
                  {props.document.checkbox_label}
                </p>
              }
            />
          </div>
          {isChecked && (
            <div>
              <P2Alert variant={"warning"} text={"Custom message based on documents!"}/>
            </div>
          )}
        </div>
      )}
      <input
        ref={inputFile}
        style={{ display: "none" }}
        type="file"
        accept=".xlsx,.xls,image/*,.doc, .docx,.ppt, .pptx,.txt,.pdf"
        multiple
        value={""}
        onChange={handleFileUploadOnChange(props.document.code, props.document.label)}
      />
      {/* <div className="polymer-small-width"> */}
      <P2Modal
          isOpen={isModalOpen}
          onCloseModal={closeModal}
          variant="default"
          title="Delete file"
          primaryButtonText="No"
          secondaryButtonText="Yes"
          primaryButtonFunction={closeModal}
          secondaryButtonFunction={secondaryButtonFunction}
          children={<p>Are you sure you want to delete <span style={{fontStyle: "italic"}}>{selectedDeleteFileName}</span> ?</p>}
        />
      {/* </div> */}
    </div>
  );
}

export default FileSelectionSegment;
